import React, { useEffect, useState } from 'react'
import {
  Alert, Button, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, FormControl, InputLabel, MenuItem, Select, Typography,
} from '@mui/material'
import { supabase } from '../../supabaseClient'
import { readFunctionError } from '../../lib/functionError'

interface ClassOption {
  class_id: string
  name:     string
}

interface Props {
  open:               boolean
  studentId:          string | null
  studentLabel:       string
  currentClassId:     string | null
  currentClassName:   string | null
  onClose:            () => void
  onMoved:            () => void
}

export default function ChangeClassDialog({
  open, studentId, studentLabel, currentClassId, currentClassName, onClose, onMoved,
}: Props) {
  const [classes,    setClasses]    = useState<ClassOption[]>([])
  const [newClassId, setNewClassId] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error,      setError]      = useState<string | null>(null)

  useEffect(() => {
    if (!open) return
    setNewClassId(''); setError(null)
    supabase
      .from('class')
      .select('class_id, name, status')
      .in('status', ['upcoming', 'active'])
      .order('name')
      .then(({ data }) => setClasses((data ?? []).filter(c => c.class_id !== currentClassId)))
  }, [open, currentClassId])

  async function handleMove() {
    if (!studentId || !newClassId) return
    setSubmitting(true)
    setError(null)
    const { data, error: invokeErr } = await supabase.functions.invoke('move-to-class', {
      body: { student_id: studentId, new_class_id: newClassId },
    })
    if (invokeErr) { setError(await readFunctionError(invokeErr)); setSubmitting(false); return }
    if (data?.error) { setError(data.error); setSubmitting(false); return }
    setSubmitting(false)
    onMoved()
    onClose()
  }

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>Change Class</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {studentLabel}
        </Typography>
        <Typography variant="body2" sx={{ mb: 2 }}>
          Current class: <strong>{currentClassName ?? '—'}</strong>
        </Typography>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <FormControl size="small" fullWidth required>
          <InputLabel>Move to</InputLabel>
          <Select label="Move to" value={newClassId} onChange={e => setNewClassId(e.target.value)}>
            {classes.length === 0 && <MenuItem value="" disabled>(no other enrollable classes)</MenuItem>}
            {classes.map(c => (
              <MenuItem key={c.class_id} value={c.class_id}>{c.name}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={submitting}>Cancel</Button>
        <Button variant="contained" onClick={handleMove} disabled={submitting || !newClassId}>
          {submitting ? <CircularProgress size={20} /> : 'Move'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
